/**
 * FINAL cookie-consent copy: DesignBot, Cookie Consent Spec §4 (Nov 5, 2026).
 * Andrew-authorized to ship without DocsBot/legal sign-off. Use these strings exactly;
 * policy version / re-consent / Privacy Policy URL are in config.ts.
 *
 * Classification decisions (TuneDevBot, Nov 3 2026), reflected in the
 * "Strictly necessary" table below:
 * - Captcha (Cloudflare Turnstile / Google reCAPTCHA v3): necessary (security / bot protection).
 * - Sentry (processor: Functional Software, Inc. d/b/a Sentry): error monitoring is
 *   strictly necessary / legitimate interest (TuneDevBot decision). Sentry.init runs
 *   before consent for error reporting ONLY: sendDefaultPii false, no IP stored
 *   (user.ip_address null, request headers/cookies/query removed, URL queries dropped),
 *   user id only, no console/DOM breadcrumbs (utils/sentry-privacy.ts). Session Replay,
 *   tracing (performance monitoring) and UI/console breadcrumbs run only after
 *   ANALYTICS consent (sentry-replay.ts). Region/retention: PENDING (see the Personal
 *   Data Inventory row "Sentry (web front end)").
 * - Device fingerprint (TF_SECFP): necessary only when used for fraud/security.
 *   Finding: apiv1/fingerprint.py + fingerprint_service.py use it for login/register
 *   bot detection and replay-protected attestation; admin uses ProfileFingerPrint to
 *   flag/deactivate linked suspicious accounts (BOT_FINGERPRINT). No analytics/ads use
 *   was found, so it is listed as necessary (security).
 */
export const COPY = {
  bannerTitle: 'We value your privacy',
  bannerBody:
    'We use strictly necessary cookies to run Tune.fm. With your permission, we also use optional cookies for analytics (Google Analytics 4) and marketing (tags fired by Google Tag Manager). You can Accept all, Reject all, or Customize by category. You can change your choices anytime via Cookie settings.',
  /** banner body links: "Cookie settings" opens the preferences modal; "Privacy Policy" -> privacy_policy_url */
  settingsLink: 'Cookie settings',
  privacyLink: 'Privacy Policy',
  rejectAll: 'Reject all',
  customize: 'Customize',
  acceptAll: 'Accept all',
  saveChoices: 'Save choices',
  modalTitle: 'Cookie preferences',
  modalIntro:
    'Choose which optional cookies Tune.fm may use. Strictly necessary cookies stay on because the service cannot work without them. Your choices are stored for 12 months, or until we change this policy.',
  closeLabel: 'Close cookie preferences',
  saved: 'Your cookie preferences were saved.',
  saveError: "We couldn't save your preferences. Try again.",
  youtubeNote: 'Playing this video loads YouTube. Continuing plays the video and allows YouTube to set cookies for that playback.',
  ccpaLink: 'Do Not Sell or Share My Personal Information',
  alwaysOn: 'Always on',
  viewCookies: 'View cookies',
  gpc: "We honored your browser's Global Privacy Control signal. Marketing cookies stay off.",
  gpcLocked: '(Off: Global Privacy Control)',
}

export interface CookieInfo {
  name: string
  provider: string
  purpose: string
  duration: string
}

export interface CategoryInfo {
  id: 'necessary' | 'analytics' | 'marketing'
  label: string
  description: string
  cookies: CookieInfo[]
}

/** Cookie tables. The Marketing list still depends on the GTM container audit (spec §3: UNKNOWN). */
export const CATEGORIES: CategoryInfo[] = [
  {
    id: 'necessary',
    label: 'Strictly necessary',
    description:
      'Required for security, sign-in, fraud prevention, consent storage, and core fonts (including Adobe Fonts). If Adobe Fonts cannot load, we fall back to system fonts. These cannot be turned off.',
    cookies: [
      { name: 'sessionid', provider: 'Tune.fm', purpose: 'Keeps you signed in', duration: 'Session / 2 weeks' },
      { name: 'csrftoken', provider: 'Tune.fm', purpose: 'Protects forms against forgery', duration: '1 year' },
      { name: 'tune_consent', provider: 'Tune.fm', purpose: 'Stores your cookie choices', duration: '12 months' },
      { name: 'TF_SECFP', provider: 'Tune.fm', purpose: 'Device check for fraud and bot protection at sign-in', duration: 'TBD' },
      { name: 'Turnstile / reCAPTCHA', provider: 'Cloudflare / Google', purpose: 'Bot protection on forms (captcha)', duration: 'Session / TBD' },
      { name: 'Adobe Fonts / Typekit (no cookies)', provider: 'Adobe', purpose: 'Core fonts: loads the landing page web fonts from use.typekit.net; Adobe logs a page view (p.typekit.net) for font licensing. System fonts are used if it cannot load', duration: 'n/a' },
      { name: 'Sentry error monitoring (no cookies)', provider: 'Functional Software, Inc. (Sentry), processor', purpose: 'Error monitoring: error details, user id, browser/OS and page URL without query; no IP address, no session replay. Legal basis: legitimate interest (strictly necessary)', duration: 'n/a (events kept per Sentry retention, TBD)' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    description:
      'Helps us understand how Tune.fm is used via Google Analytics 4 (GA4 measurement ID G-6689026RCH). Includes product analytics and Sentry Session Replay / tracing when enabled. Off until you opt in.',
    cookies: [
      { name: '_ga, _ga_*', provider: 'Google', purpose: 'Distinguishes visitors and sessions', duration: 'Up to 14 months' },
      { name: 'Sentry Session Replay (sessionStorage)', provider: 'Functional Software, Inc. (Sentry), processor', purpose: 'Records page interactions to debug errors (media blocked). Only with analytics consent', duration: 'Session' },
      { name: 'Sentry performance tracing and UI/console breadcrumbs (no cookies)', provider: 'Functional Software, Inc. (Sentry), processor', purpose: 'Page-load and request timings, clicks and console messages attached to error reports. Only with analytics consent', duration: 'n/a' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description:
      'Lets Google Tag Manager (GTM-W5LBRNP4) fire advertising and attribution tags (for example ad pixels). Off until you opt in. When Global Privacy Control (GPC) is on, marketing stays off.',
    cookies: [
      { name: 'TBD (GTM container audit)', provider: 'Google / third parties', purpose: 'Ads measurement', duration: 'TBD' },
    ],
  },
]
